/**
 * QueryInputRow — the unified query-input surface.
 *
 * SPD v5.86 (2026-02-14) · operator directive:
 *   "The subsequent query 2, 3, 4+ etc field should work exactly like
 *    the primary (1st Query area). Any next step, prompt, reasoning,
 *    results should appear below the relevant query. Back to the top,
 *    download, clear should all appear in same location."
 *
 * This component replaces both the old FollowUpInput at the page
 * bottom AND the small "Q:" bubble that used to sit in the top-left
 * of every historical thread item. Every query — the primary at the
 * top of the page, each historical query in the thread, and the
 * active empty next-query input at the bottom — now uses THIS
 * component. Same font size, same magnifier icon, same typeahead
 * surface, same interaction rules. One source of truth.
 *
 * Two rendering modes:
 *
 *   isReadOnly === true
 *     Renders the query text with the magnifier icon inline right
 *     after it. No textarea, no typeahead, no input handlers. Used
 *     for historical queries in the thread — they display what was
 *     searched but are not interactive.
 *
 *   isReadOnly === false (default)
 *     Renders the full active input: three-layer transparency
 *     textarea + magnifier button + typeahead dropdown surface below.
 *     Used for the primary input at the top of the page AND the
 *     active empty input at the bottom of the thread.
 *
 * Ambient predictive-completion callbacks (onPickChip / onPickCompletion
 * / onPickFreetext) route through the parent's compare-default + location-
 * consent guard (see KnowledgeGraphPage.jsx submitPrimaryQuery for the
 * canonical implementation). Enter and magnifier click both fire the
 * parent-supplied onSubmit — the parent decides what to do with the
 * submitted text.
 */
import React from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';
import { TypeaheadDropdown } from './TypeaheadDropdown';
import { CompletionsSurface } from './CompletionsSurface';
import { shouldShowOptionsView } from '../../hooks/optionsViewPredicate';

export const QueryInputRow = ({
  value,
  setValue,
  onSubmit,
  isReadOnly = false,
  isQuerying = false,
  inputRef,
  typeaheadState = null,
  completionsState = null,
  onPickChip,
  onPickCompletion,
  onPickFreetext,
  placeholder = 'Search-it',
  dataTestId = 'query-input-row',
}) => {
  const isMobile = useIsMobile(640);

  const fontSize = isMobile ? '1.125rem' : '1.875rem';
  const lineHeight = isMobile ? '1.5rem' : '2.4rem';
  const magH = isMobile ? 'h-[20px]' : 'h-[32px]';
  const magW = isMobile ? 'w-[20px]' : 'w-[32px]';
  const spinnerSize = isMobile ? 'h-[18px] w-[18px]' : 'h-[28px] w-[28px]';

  // ─── Read-only display (historical queries in the thread) ─────────
  if (isReadOnly) {
    return (
      <div
        className={`${isMobile ? 'px-3' : 'px-6'} py-3`}
        data-testid={`${dataTestId}-readonly`}
      >
        <div
          className="flex items-start"
          style={isMobile ? { marginLeft: '12px', marginRight: '12px' } : { marginLeft: '82px', marginRight: '82px' }}
        >
          <div className="flex-1 relative">
            <div className="flex items-baseline" style={{ fontSize, lineHeight }}>
              <span className="text-white" style={{ wordBreak: 'break-word' }}>
                {value}
              </span>
              <img
                src="/icons/magnifier.svg"
                alt="Search"
                className={`${magH} ${magW} ${isMobile ? 'ml-1' : 'ml-2'} flex-shrink-0 opacity-60`}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Active input (primary + empty next-query) ────────────────────
  return (
    <div
      className={`${isMobile ? 'px-3' : 'px-6'} flex items-start`}
      style={{ paddingTop: '2vh', paddingBottom: '2vh' }}
      data-testid={`${dataTestId}-active`}
    >
      <div
        className="flex items-start w-full"
        style={isMobile ? { marginLeft: '12px', marginRight: '12px' } : { marginLeft: '82px', marginRight: '82px' }}
      >
        <div className="flex-1 relative">
          {/* Layer 1/3 · display or placeholder */}
          {!value ? (
            <div
              className="flex items-center text-gray-400 opacity-70 pointer-events-none relative z-20"
              style={{ fontSize, lineHeight }}
              data-testid={`${dataTestId}-placeholder`}
            >
              <span className="blinking-cursor mr-1"></span>
              <span>{placeholder}</span>
              <img
                src="/icons/magnifier.svg"
                alt="Search"
                className={`${magH} ${magW} ${isMobile ? 'ml-1' : 'ml-2'} flex-shrink-0`}
              />
            </div>
          ) : (
            <div
              className="relative z-20"
              style={{ fontSize, lineHeight }}
              data-testid={`${dataTestId}-display`}
            >
              <span className="text-white" style={{ wordBreak: 'break-word' }}>
                {value}
              </span>
              <button
                type="button"
                onClick={onSubmit}
                disabled={isQuerying}
                /* SPD v5.86 hotfix — z-20 lifts the button above the
                   transparent-text textarea below (D8 3-layer trick).
                   Without this the click lands on the textarea and is
                   swallowed. */
                className={`${isMobile ? 'ml-1' : 'ml-2'} p-1 transition-transform hover:scale-105 disabled:opacity-50 inline-flex items-center align-middle relative z-20`}
                data-testid={`${dataTestId}-search-btn`}
              >
                <img
                  src="/icons/magnifier.svg"
                  alt="Search"
                  className={`${magH} ${magW}`}
                />
                {isQuerying && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className={`animate-spin ${spinnerSize} border-2 border-cyan-400 border-t-transparent rounded-full`}></div>
                  </div>
                )}
              </button>
            </div>
          )}

          {/* Layer 2 · transparent-text textarea for caret + input */}
          <textarea
            ref={inputRef}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
            rows={1}
            className="absolute top-0 left-0 w-full bg-transparent border-none outline-none resize-none z-10"
            style={{
              color: 'transparent',
              WebkitTextFillColor: 'transparent',
              textShadow: 'none',
              caretColor: 'white',
              fontSize,
              lineHeight,
              fontFamily: 'inherit',
              fontWeight: 'inherit',
              letterSpacing: 'inherit',
              padding: 0,
              margin: 0,
            }}
            data-testid={`${dataTestId}-textarea`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSubmit();
              }
            }}
          />

          {/* SPD v5.87 · Predictive Completions (2026-02-14).
              Client-side trie ranker renders inline BEFORE the LLM
              typeahead chips. Sub-100 ms hard-cap first paint. Click
              → replace input + fire search (parent's onPickCompletion
              handles the routing).

              IJ-238 (2026-08-01) · REDLINE-15 mutual exclusion is
              enforced here at the render root: suppress this surface
              when the LLM classifier signals Options view (vague
              query · low intent_confidence) OR a refused query, so
              the trie completions never render alongside the LLM
              OptionsPrimitive grid and never leak past a refusal. */}
          {completionsState && !isQuerying &&
           !(typeaheadState && (shouldShowOptionsView(typeaheadState) || typeaheadState.query_class === 'refused')) && (
            <CompletionsSurface
              state={completionsState}
              onPick={(text) => onPickCompletion && onPickCompletion(text)}
              dataTestId={`${dataTestId}-completions`}
            />
          )}

          {/* Ambient Intent v3 typeahead — passes `data` prop so the
              new PrimitiveByLayoutHint tree renders (2-col chips +
              Other freetext + LLM intro_line). */}
          {typeaheadState && (
            <TypeaheadDropdown
              data={typeaheadState}
              onPickChip={onPickChip}
              onPickCompletion={onPickCompletion}
              onPickFreetext={onPickFreetext}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default QueryInputRow;
